/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import EffortTextV2, { getEffortTextV2 } from 'bundles/course-frame/components/course-outline/EffortTextV2';
import LockIcon from 'bundles/course-frame/components/icons/LockIcon';
import type { Item } from 'bundles/learner-progress/types/Item';
import { getIsEvaluable, getIsReviewPartForSplitPeer } from 'bundles/learner-progress/utils/Item';
import { truncToPlace } from 'bundles/primitive-formatting/utils/numberUtils';

import _t from 'i18n!nls/course-frame';

const styles = {
  preDot: css`
    &::before {
      content: '•';
      margin-left: 4px;
      margin-right: 4px;
    }
  `,
  failed: css`
    color: var(--cds-color-feedback-error);
  `,
  lockIcon: css`
    vertical-align: sub;
  `,
};

// There are basically 2 duplicates in this file, when making changes, make sure to update both.
// The first one is intended to render a string for aria-label etc.
// The second one is intended to render JSX for UI elements.
// They both share the same input "props".
type Props = {
  computedItem: Item;
  isFreemiumLocked: boolean;
  isLocked: boolean;
  isPhase3Enabled: boolean;
};

/**
 * Logic to render one thing after the item type in the new Course Frame navigation.
 * It can be the time estimate, or a grading status, or a grade, or in some situations, nothing.
 * @returns string, intended for use for aria-label etc. See ItemNavigationLabel to render a React element.
 */
export const getItemNavigationLabel = (props: Props): string | undefined => {
  const { computedItem, isFreemiumLocked, isLocked, isPhase3Enabled } = props;

  if (getIsReviewPartForSplitPeer(computedItem) || computedItem?.isVerifiedSkillAssessment) {
    return undefined;
  }

  // We do not render anything except the item name for verified skill diagnostic items
  if (computedItem?.isVerifiedSkillDiagnostic) {
    return undefined;
  }

  if (isPhase3Enabled) {
    if (isLocked && !isFreemiumLocked) {
      // 'Locked' is already being added to `aria-label` of a nav link
      // by `getStatusIconLabel(computedItem)`
      // We also want to avoid falling back to returning some of the other text below.
      return undefined;
    }

    if (computedItem.isFailed) {
      // 'Failed' is already being added to `aria-label` of a nav link
      // by `getStatusIconLabel(computedItem)`
      // We also want to avoid falling back to returning some of the other text below.
      return undefined;
    }
  }

  if (getIsEvaluable(computedItem) && computedItem.isSubmitted && !computedItem.isGraded) {
    return _t('Results pending');
  }

  if (computedItem.isSubmitted && computedItem.isGraded) {
    const grade = computedItem.itemGrade.get('overallOutcome').grade;
    const gradePercentNumber = truncToPlace(grade * 100, 2);
    return _t('Grade: #{gradePercentNumber}%', { gradePercentNumber });
  }

  return getEffortTextV2(computedItem);
};

/**
 * Logic to render one thing after the item type in the new Course Frame navigation.
 * It can be the time estimate, or a grading status, or a grade, or in some situations, nothing.
 * @returns React element. For a string, see getItemNavigationLabel.
 */
export const ItemNavigationLabel = (props: Props) => {
  const { computedItem, isFreemiumLocked, isLocked, isPhase3Enabled } = props;

  if (
    getIsReviewPartForSplitPeer(computedItem) ||
    computedItem?.isVerifiedSkillAssessment ||
    computedItem?.isVerifiedSkillDiagnostic
  ) {
    return null;
  }

  if (isPhase3Enabled) {
    if (isLocked && !isFreemiumLocked) {
      // If it's freemium-locked, the entire module is visually locked, we don't need one at the item level.
      // We want to fall back to rendering below (most likely EffortTextV2)
      return (
        <span css={styles.preDot}>
          <LockIcon css={styles.lockIcon} />
          {_t('Locked')}
        </span>
      );
    }

    if (computedItem.isFailed) {
      return (
        <span css={styles.preDot}>
          <span css={styles.failed}>{_t('Failed')}</span>
        </span>
      );
    }
  }

  if (getIsEvaluable(computedItem) && computedItem.isSubmitted && !computedItem.isGraded) {
    return <span css={styles.preDot}>{_t('Results pending')}</span>;
  }

  if (computedItem.isGraded) {
    const grade = computedItem.itemGrade.get('overallOutcome').grade;
    const gradePercentNumber = truncToPlace(grade * 100, 2);
    return <span css={styles.preDot}>{_t('Grade: #{gradePercentNumber}%', { gradePercentNumber })}</span>;
  }

  return <EffortTextV2 computedItem={computedItem} css={styles.preDot} />;
};

export default ItemNavigationLabel;
